import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

export interface WordUsageStats {
  charactersUsed: number;
  wordsUsed: number;
  wordsLimit: number;
  lastResetDate: string;
  usagePercentage: number;
  isOverLimit: boolean;
  // Additional user activity statistics
  lastActiveAt?: string;
  sessionCount?: number;
  totalAudioMinutes?: number;
  totalTranscriptions?: number;
  totalTranslations?: number;
  totalWordsTranscribed?: number;
  totalWordsTranslated?: number;
  totalCharactersTranscribed?: number;
  totalCharactersTranslated?: number;
  // Toggle preference fields
  showRemainingView?: boolean;
  wordsUsedAtLastPurchase?: number;
}

export interface QuotaSettings {
  wordLimit: number;
  resetPeriod: 'daily' | 'weekly' | 'monthly' | 'never';
  enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WordUsageService {
  private currentUserId: string = 'anonymous';
  private hasLoggedUnauthenticated = false;
  private isInitialized = false; // Flag to prevent double initialization
  private isLoading = false; // Flag to prevent concurrent loading calls
  private isLoadingStats = false; // Flag to prevent concurrent stats loading
  private isLoadingQuota = false; // Flag to prevent concurrent quota loading
  private usageStatsSubject = new BehaviorSubject<WordUsageStats>(
    this.getDefaultStats()
  );
  public usageStats$ = this.usageStatsSubject.asObservable();

  private quotaSettingsSubject = new BehaviorSubject<QuotaSettings>(
    this.getDefaultSettings()
  );
  public quotaSettings$ = this.quotaSettingsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    console.log('🔧 WordUsageService constructor called');
    // Initialize with defaults first
    const defaultStats = this.getDefaultStats();
    const defaultSettings = this.getDefaultSettings();
    console.log('📊 Initializing with default stats:', defaultStats);
    console.log('⚙️ Initializing with default settings:', defaultSettings);

    this.usageStatsSubject.next(defaultStats);
    this.quotaSettingsSubject.next(defaultSettings);

    // Subscribe to authentication changes to reload data when user logs in
    this.authService.currentUser$.subscribe((user: User | null) => {
      if (user) {
        const newUserId = (user as any).id || user.email || 'anonymous';
        // Check if user actually changed
        if (newUserId !== this.currentUserId) {
          console.log(
            `👤 User changed from "${this.currentUserId}" to "${newUserId}", reloading data...`
          );
          // Reset initialization flag to allow reload
          this.isInitialized = false;
          this.currentUserId = newUserId;
          // Clear cached headers to force fresh token fetch
          this.cachedHeaders = null;
          this.headersCacheTime = 0;
          // Reload data for new user
          this.checkAuthAndLoadData();
        } else if (!this.isInitialized) {
          // Same user but not initialized yet
          console.log('👤 User logged in (same user), loading data...');
          this.isInitialized = false;
          this.checkAuthAndLoadData();
        }
      } else {
        console.log('👤 User logged out, resetting to defaults...');
        this.isInitialized = false;
        this.currentUserId = 'anonymous';
        // Clear cached headers
        this.cachedHeaders = null;
        this.headersCacheTime = 0;
        this.usageStatsSubject.next(this.getDefaultStats());
        this.quotaSettingsSubject.next(this.getDefaultSettings());
      }
    });

    // Check if user is authenticated before trying to load from API
    this.checkAuthAndLoadData();
  }

  private async checkAuthAndLoadData(): Promise<void> {
    // Prevent double initialization and concurrent loading
    if (this.isInitialized) {
      console.log('🔐 WordUsageService: Already initialized, skipping...');
      return;
    }

    // Prevent concurrent calls
    if (this.isLoading) {
      console.log(
        '🔐 WordUsageService: Already loading, skipping duplicate call...'
      );
      return;
    }

    this.isLoading = true;

    try {
      console.log('🔐 WordUsageService: Checking authentication...');

      // For iOS, add a small delay to ensure localStorage is ready
      if (Capacitor.getPlatform() === 'ios') {
        console.log(
          '📱 iOS detected, adding delay for localStorage readiness...'
        );
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const token = await this.authService.getAuthToken();
      console.log('🔐 WordUsageService: Token result:', !!token);

      if (token) {
        // Reset the flag when user is authenticated
        this.hasLoggedUnauthenticated = false;
        console.log(
          '🔐 WordUsageService: User is authenticated, loading data from API'
        );
        console.log('📱 Platform:', Capacitor.getPlatform());
        console.log('🌐 API URL:', environment.apiUrl);
        this.loadUsageStats();
        this.loadQuotaSettings();
        this.isInitialized = true;
      } else {
        // Only log this once per session to reduce console noise
        if (!this.hasLoggedUnauthenticated) {
          console.log(
            'ℹ️ WordUsageService: User not authenticated, using default data (this is normal when logged out)'
          );
          console.log('📱 Platform:', Capacitor.getPlatform());
          console.log(
            '🔍 localStorage auth0 keys:',
            Object.keys(localStorage).filter((k) => k.includes('auth0'))
          );
          this.hasLoggedUnauthenticated = true;
        }
        this.isInitialized = true; // Mark as initialized even for unauthenticated users
      }
    } catch (error) {
      console.error(
        '❌ WordUsageService: Error checking authentication:',
        error
      );
      this.isInitialized = true; // Mark as initialized even on error
    } finally {
      this.isLoading = false; // Always reset loading flag
    }
  }

  setCurrentUser(userId: string): void {
    const previousUserId = this.currentUserId;
    this.currentUserId = userId || 'anonymous';
    console.log(
      '👤 WordUsageService: Setting current user:',
      this.currentUserId
    );

    // Only reload data if the user actually changed
    if (previousUserId !== this.currentUserId) {
      console.log(
        '👤 WordUsageService: User changed, resetting initialization flag'
      );
      this.isInitialized = false;
      this.checkAuthAndLoadData();
    }
  }

  private getDefaultStats(): WordUsageStats {
    return {
      charactersUsed: 0,
      wordsUsed: 0,
      wordsLimit: 0, // No default limit - will be set by backend
      lastResetDate: new Date().toISOString().split('T')[0],
      usagePercentage: 0,
      isOverLimit: false,
      // Initialize additional statistics
      lastActiveAt: new Date().toISOString(),
      sessionCount: 0,
      totalAudioMinutes: 0,
      totalTranscriptions: 0,
      totalTranslations: 0,
      totalWordsTranscribed: 0,
      totalWordsTranslated: 0,
      totalCharactersTranscribed: 0,
      totalCharactersTranslated: 0,
    };
  }

  private getDefaultSettings(): QuotaSettings {
    return {
      wordLimit: 0, // No default limit - will be set by backend
      resetPeriod: 'monthly',
      enabled: true, // Enable quota system by default
    };
  }

  private cachedHeaders: HttpHeaders | null = null;
  private headersCacheTime: number = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds cache
  private getAuthHeadersPromise: Promise<HttpHeaders> | null = null; // Lock to prevent concurrent calls

  private async getAuthHeaders(): Promise<HttpHeaders> {
    // Check if we have cached headers that are still valid
    const now = Date.now();
    if (
      this.cachedHeaders &&
      now - this.headersCacheTime < this.CACHE_DURATION
    ) {
      // Return cached headers without logging to reduce noise
      return this.cachedHeaders;
    }

    // If there's already a pending request, return it instead of creating a new one
    if (this.getAuthHeadersPromise) {
      return this.getAuthHeadersPromise;
    }

    // Create a new promise and store it as a lock
    this.getAuthHeadersPromise = (async () => {
      try {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
        });

        try {
          const token = await this.authService.getAuthToken();

          if (token) {
            // Check if token looks valid (basic validation)
            if (
              token.length > 10 &&
              !token.includes('undefined') &&
              !token.includes('null')
            ) {
              this.cachedHeaders = headers.set(
                'Authorization',
                `Bearer ${token}`
              );
              this.headersCacheTime = Date.now();
              return this.cachedHeaders;
            } else {
              console.warn(
                '⚠️ WordUsageService: Token appears invalid, using anonymous mode'
              );
            }
          } else {
            console.warn(
              '⚠️ WordUsageService: No auth token available - using anonymous mode'
            );
          }
        } catch (error) {
          console.error(
            '❌ WordUsageService: Error getting auth token:',
            error
          );
          console.warn(
            '⚠️ WordUsageService: Using anonymous mode due to auth error'
          );
        }

        // Use anonymous mode
        this.cachedHeaders = headers;
        this.headersCacheTime = Date.now();
        return headers;
      } finally {
        // Clear the promise lock when done
        this.getAuthHeadersPromise = null;
      }
    })();

    return this.getAuthHeadersPromise;
  }

  private loadUsageStats(): void {
    // Prevent concurrent calls
    if (this.isLoadingStats) {
      console.log(
        '🔐 WordUsageService: Stats already loading, skipping duplicate call...'
      );
      return;
    }

    this.isLoadingStats = true;

    // Try to load from API, fallback to defaults if not available
    this.getUsageStatsFromAPI().subscribe({
      next: (stats) => {
        console.log(
          '✅ WordUsageService: Successfully loaded usage stats from API:',
          stats
        );
        // Recalculate isOverLimit and usagePercentage to ensure accuracy
        this.updateUsagePercentage(stats);
        console.log('🔄 Updating usageStatsSubject with new data');
        this.usageStatsSubject.next(stats);
      },
      error: (error) => {
        if (error.status === 401 || error.status === 403) {
          console.warn(
            `🔐 WordUsageService: Authentication failed (${error.status}) - user may need to re-login`
          );
          // Handle authentication error with automatic logout
          this.handleAuthError(error);
        } else {
          console.warn(
            '⚠️ WordUsageService: API not available, preserving existing stats:',
            error.status
          );
        }
        // DO NOT reset to defaults on API error - preserve existing stats
        // Only reset if we don't have any existing stats (first load)
        const currentStats = this.usageStatsSubject.value;
        if (!currentStats || (currentStats.wordsUsed === 0 && currentStats.charactersUsed === 0)) {
          console.log('⚠️ No existing stats found, using defaults');
          this.usageStatsSubject.next(this.getDefaultStats());
        } else {
          console.log('✅ Preserving existing stats on API error:', currentStats);
          // Keep existing stats - don't overwrite with zeros
        }
        this.isLoadingStats = false; // Reset flag on error
      },
      complete: () => {
        this.isLoadingStats = false; // Reset flag when subscription completes
      },
    });
  }

  private loadQuotaSettings(): void {
    // Prevent concurrent calls
    if (this.isLoadingQuota) {
      console.log(
        '🔐 WordUsageService: Quota already loading, skipping duplicate call...'
      );
      return;
    }

    this.isLoadingQuota = true;

    // Try to load from API, fallback to defaults if not available
    this.getQuotaSettingsFromAPI().subscribe({
      next: (settings) => {
        console.log(
          '✅ WordUsageService: Successfully loaded quota settings from API'
        );
        this.quotaSettingsSubject.next(settings);
        // Recalculate stats when settings are updated
        const currentStats = this.usageStatsSubject.value;
        if (
          currentStats &&
          (currentStats.wordsUsed > 0 || currentStats.charactersUsed > 0)
        ) {
          console.log('🔄 Recalculating stats after quota settings loaded');
          this.updateUsagePercentage(currentStats);
          this.usageStatsSubject.next(currentStats);
        }
      },
      error: (error) => {
        if (error.status === 401 || error.status === 403) {
          console.warn(
            `🔐 WordUsageService: Authentication failed (${error.status}) - user may need to re-login`
          );
          // Handle authentication error with automatic logout
          this.handleAuthError(error);
        } else {
          console.warn(
            '⚠️ WordUsageService: API not available, using defaults:',
            error.status
          );
        }
        // Use default settings when API is not available
        this.quotaSettingsSubject.next(this.getDefaultSettings());
        this.isLoadingQuota = false; // Reset flag on error
      },
      complete: () => {
        this.isLoadingQuota = false; // Reset flag when subscription completes
      },
    });
  }

  // API Methods
  private getUsageStatsFromAPI(): Observable<WordUsageStats> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .get<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/stats`,
            { headers }
          )
          .pipe(
            map((response) => response.data),
            catchError((error) => {
              console.error('Error fetching usage stats:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  private getQuotaSettingsFromAPI(): Observable<QuotaSettings> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .get<{ success: boolean; data: QuotaSettings }>(
            `${environment.apiUrl}/api/word-usage/settings`,
            { headers }
          )
          .pipe(
            map((response) => response.data),
            catchError((error) => {
              console.error('Error fetching quota settings:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  private shouldResetUsage(lastResetDate: string): boolean {
    const settings = this.quotaSettingsSubject.value;
    if (settings.resetPeriod === 'never') return false;

    const lastReset = new Date(lastResetDate);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24)
    );

    switch (settings.resetPeriod) {
      case 'daily':
        return diffInDays >= 1;
      case 'weekly':
        return diffInDays >= 7;
      case 'monthly':
        return diffInDays >= 30;
      default:
        return false;
    }
  }

  private updateUsagePercentage(stats: WordUsageStats): void {
    const settings = this.quotaSettingsSubject.value;

    console.log('🔧 updateUsagePercentage called:', {
      wordsUsed: stats.wordsUsed,
      wordsLimit: stats.wordsLimit,
      settingsEnabled: settings.enabled,
      previousIsOverLimit: stats.isOverLimit,
    });

    // Calculate word percentage (only word limit is checked)
    const wordPercentage =
      settings.enabled && stats.wordsLimit > 0
        ? (stats.wordsUsed / stats.wordsLimit) * 100
        : 0;

    console.log('🔧 Calculated word percentage:', wordPercentage);

    // Use word percentage for usage
    stats.usagePercentage = Math.min(wordPercentage, 100);

    // Check if over word limit (only word limit is checked)
    // Only check if quota system is enabled
    if (!settings.enabled) {
      console.log('🔧 Quota system disabled, setting isOverLimit to false');
      stats.isOverLimit = false;
      return;
    }

    const isOverWordLimit =
      stats.wordsLimit > 0 && stats.wordsUsed >= stats.wordsLimit;

    console.log('🔧 Word limit check:', {
      isOverWordLimit,
      wordsCheck: `${stats.wordsUsed} >= ${stats.wordsLimit}`,
    });

    stats.isOverLimit = isOverWordLimit;

    console.log('🔧 Final result:', {
      isOverLimit: stats.isOverLimit,
      usagePercentage: stats.usagePercentage,
    });
  }

  // Public methods
  addUsage(characters: number, words?: number): Observable<boolean> {
    const usageData = {
      characters,
      words: words || this.estimateWordsFromCharacters(characters),
    };

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/add`,
            usageData,
            { headers }
          )
          .pipe(
            map((response) => {
              if (response.success) {
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data);
                this.usageStatsSubject.next(response.data);
                return true;
              }
              return false;
            }),
            catchError((error) => {
              if (error.status === 404) {
                // API not available - silently skip tracking
                console.warn('Word usage API not available, skipping tracking');
                return [true]; // Return success to not break the flow
              }
              if (error.status === 429) {
                // Quota exceeded
                return throwError(() => new Error('Quota exceeded'));
              }
              console.error('Error adding word usage:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        if (error.status === 404) {
          // API not available - silently skip tracking
          console.warn('Word usage API not available, skipping tracking');
          return [true]; // Return success to not break the flow
        }
        return throwError(() => error);
      })
    );
  }

  private estimateWordsFromCharacters(characters: number): number {
    // Rough estimate: 5 characters per word on average
    return Math.ceil(characters / 5);
  }

  canUseQuota(characters: number): Observable<boolean> {
    const usageData = {
      characters,
      words: this.estimateWordsFromCharacters(characters),
    };

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: { canUse: boolean } }>(
            `${environment.apiUrl}/api/word-usage/check`,
            usageData,
            { headers }
          )
          .pipe(
            map((response) => response.data.canUse),
            catchError((error) => {
              if (error.status === 404) {
                // API not available - allow usage
                console.warn('Word usage API not available, allowing usage');
                return [true];
              }
              console.error('Error checking quota availability:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        if (error.status === 404) {
          // API not available - allow usage
          console.warn('Word usage API not available, allowing usage');
          return [true];
        }
        return throwError(() => error);
      })
    );
  }

  resetUsageStats(): Observable<WordUsageStats> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/reset`,
            {},
            { headers }
          )
          .pipe(
            map((response) => {
              if (response.success) {
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data);
                this.usageStatsSubject.next(response.data);
                return response.data;
              }
              throw new Error('Failed to reset usage stats');
            }),
            catchError((error) => {
              if (error.status === 404) {
                // API not available - reset locally
                console.warn('Word usage API not available, resetting locally');
                const defaultStats = this.getDefaultStats();
                this.usageStatsSubject.next(defaultStats);
                return [defaultStats];
              }
              console.error('Error resetting usage stats:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        if (error.status === 404) {
          // API not available - reset locally
          console.warn('Word usage API not available, resetting locally');
          const defaultStats = this.getDefaultStats();
          this.usageStatsSubject.next(defaultStats);
          return [defaultStats];
        }
        return throwError(() => error);
      })
    );
  }

  updateQuotaSettings(
    settings: QuotaSettings
  ): Observable<{ stats: WordUsageStats; settings: QuotaSettings }> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .put<{
            success: boolean;
            data: { stats: WordUsageStats; settings: QuotaSettings };
          }>(`${environment.apiUrl}/api/word-usage/settings`, settings, {
            headers,
          })
          .pipe(
            map((response) => {
              if (response.success) {
                this.quotaSettingsSubject.next(response.data.settings);
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data.stats);
                this.usageStatsSubject.next(response.data.stats);
                return response.data;
              }
              throw new Error('Failed to update quota settings');
            }),
            catchError((error) => {
              if (error.status === 404) {
                // API not available - update locally
                console.warn('Word usage API not available, updating locally');
                const updatedStats = { ...this.getDefaultStats(), ...settings };
                this.quotaSettingsSubject.next(settings);
                this.usageStatsSubject.next(updatedStats);
                return [{ stats: updatedStats, settings }];
              }
              console.error('Error updating quota settings:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        if (error.status === 404) {
          // API not available - update locally
          console.warn('Word usage API not available, updating locally');
          const updatedStats = { ...this.getDefaultStats(), ...settings };
          this.quotaSettingsSubject.next(settings);
          this.usageStatsSubject.next(updatedStats);
          return [{ stats: updatedStats, settings }];
        }
        return throwError(() => error);
      })
    );
  }

  getUsageStats(): WordUsageStats {
    const usageStats = this.usageStatsSubject.value;
    const quotaSettings = this.quotaSettingsSubject.value;

    // Merge quota settings into usage stats to ensure wordsLimit is available
    return {
      ...usageStats,
      wordsLimit: quotaSettings.wordLimit || usageStats.wordsLimit || 0,
    };
  }

  getQuotaSettings(): QuotaSettings {
    return this.quotaSettingsSubject.value;
  }

  // Update quota settings directly from API response (used when payment is confirmed)
  updateQuotaSettingsFromResponse(quotaSettings: QuotaSettings): void {
    console.log(
      '📊 WordUsageService: Updating quota settings from API response:',
      quotaSettings
    );
    this.quotaSettingsSubject.next(quotaSettings);

    // Trigger a full refresh immediately to get both quota settings and usage stats
    // This ensures we have the latest data from the database
    console.log(
      '📊 WordUsageService: Triggering immediate refresh after quota settings update'
    );
    this.loadUsageStats();
    this.loadQuotaSettings();
  }

  // Refresh data from server - always fetch fresh data from API
  refreshData(): void {
    console.log('🔄 WordUsageService: Refreshing data from backend API...');
    console.log(
      '🔄 Current cached stats before refresh:',
      this.usageStatsSubject.value
    );
    
    // Reset initialization flags to force fresh API call
    this.isInitialized = false;
    this.isLoading = false;
    this.isLoadingStats = false;
    this.isLoadingQuota = false;
    
    // Clear cached headers to force fresh token fetch
    this.cachedHeaders = null;
    this.headersCacheTime = 0;
    
    // Directly call API methods to fetch fresh data
    this.loadUsageStats();
    this.loadQuotaSettings();
  }

  /**
   * Force refresh data for iOS compatibility
   */
  async forceRefreshData(): Promise<void> {
    console.log('🔄 WordUsageService: Force refreshing data for iOS...');
    this.isInitialized = false;
    this.isLoading = false; // Reset loading flag to allow refresh

    // DO NOT reset stats to zero - preserve existing data until new data loads
    // This prevents word counts from being reset if API call fails or is slow
    console.log('🔄 Preserving current stats during refresh:', this.usageStatsSubject.value);

    // Wait a bit for iOS localStorage to be ready
    if (Capacitor.getPlatform() === 'ios') {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    await this.checkAuthAndLoadData();
  }

  // Format usage for display
  formatUsage(
    usage: WordUsageStats,
    showRemainingView: boolean = false,
    wordsUsedAtLastPurchase: number = 0
  ): {
    charactersText: string;
    wordsText: string;
    percentageText: string;
    statusText: string;
    statusColor: string;
  } {
    const settings = this.quotaSettingsSubject.value;

    // Ensure we have valid numbers with fallbacks for old accounts
    const charactersUsed = usage.charactersUsed || 0;
    const wordsUsed = usage.wordsUsed || 0;
    const usagePercentage = usage.usagePercentage || 0;

    // Check if there's actually a word limit set (regardless of enabled flag)
    if (!settings.enabled && (!settings.wordLimit || settings.wordLimit <= 0)) {
      return {
        charactersText: `${charactersUsed.toLocaleString()} characters used`,
        wordsText: `${wordsUsed.toLocaleString()} words used`,
        percentageText: 'No limit set',
        statusText: 'Unlimited',
        statusColor: 'text-green-600',
      };
    }

    let wordPercentage = 0;
    let wordsText = '';
    let statusText = 'Within limit';
    let statusColor = 'text-green-600';

    if (showRemainingView) {
      // Remaining view: show countdown from 0 to remaining words
      const wordsRemaining = Math.max(0, settings.wordLimit - wordsUsed);
      wordPercentage =
        settings.wordLimit > 0
          ? Math.round((wordsRemaining / settings.wordLimit) * 100)
          : 0;
      wordsText = `0 to ${wordsRemaining.toLocaleString()} words`;

      console.log('🔍 DEBUG: Remaining view calculation:', {
        wordsUsed,
        wordsUsedAtLastPurchase,
        wordsRemaining,
        settingsWordLimit: settings.wordLimit,
        wordPercentage,
        wordsText,
      });
    } else {
      // Total view: show total usage vs total limit
      wordPercentage =
        settings.wordLimit > 0
          ? Math.round((wordsUsed / settings.wordLimit) * 100)
          : 0;
      wordsText = `${wordsUsed.toLocaleString()} / ${settings.wordLimit.toLocaleString()} words`;

      // Debug log removed to prevent multiple console outputs
    }

    if (usage.isOverLimit) {
      statusText = 'Quota exceeded';
      statusColor = 'text-red-600';
    } else if (wordPercentage >= 80) {
      statusText = 'Near limit';
      statusColor = 'text-yellow-600';
    }

    return {
      charactersText: `${charactersUsed.toLocaleString()} characters used`,
      wordsText,
      percentageText: showRemainingView
        ? `${wordPercentage}% remaining`
        : `${wordPercentage}% used`,
      statusText,
      statusColor,
    };
  }

  // Track transcription activity
  trackTranscription(
    transcribedText: string,
    audioMinutes: number = 0
  ): Observable<boolean> {
    // Validate input on frontend
    if (!transcribedText || typeof transcribedText !== 'string') {
      console.warn(
        '⚠️ Invalid transcribed text for tracking:',
        transcribedText
      );
      return throwError(() => new Error('Invalid transcribed text'));
    }
    if (
      audioMinutes < 0 ||
      typeof audioMinutes !== 'number' ||
      !isFinite(audioMinutes)
    ) {
      console.warn('⚠️ Invalid audio minutes for tracking:', audioMinutes);
      return throwError(() => new Error('Invalid audio minutes'));
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/track-transcription`,
            { transcribedText, audioMinutes },
            { headers }
          )
          .pipe(
            map((response) => {
              if (response.success) {
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data);
                this.usageStatsSubject.next(response.data);
                console.log(
                  '🎤 Transcription tracked successfully:',
                  'text: "' + transcribedText + '"',
                  audioMinutes,
                  'minutes'
                );
                return true;
              }
              return false;
            }),
            catchError((error) => {
              console.error('❌ Failed to track transcription:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        console.error(
          '❌ Failed to get auth headers for transcription tracking:',
          error
        );
        return throwError(() => error);
      })
    );
  }

  // Track translation activity (only count words in translated output)
  trackTranslation(translatedText: string): Observable<boolean> {
    // Validate input on frontend
    if (!translatedText || typeof translatedText !== 'string') {
      console.warn('⚠️ Invalid translated text for tracking:', translatedText);
      return throwError(() => new Error('Invalid translated text'));
    }

    console.log('🔍 WordUsageService sending HTTP request:');
    console.log('  translatedText length:', translatedText.length);

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/track-translation`,
            { translatedText },
            { headers }
          )
          .pipe(
            map((response) => {
              if (response.success) {
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data);
                this.usageStatsSubject.next(response.data);
                console.log(
                  '🔄 Translation tracked successfully:',
                  'translated: "' + translatedText + '"'
                );
                return true;
              }
              return false;
            }),
            catchError((error) => {
              console.error('❌ Failed to track translation:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        console.error(
          '❌ Failed to get auth headers for translation tracking:',
          error
        );
        return throwError(() => error);
      })
    );
  }

  // Track user session
  trackSession(): Observable<boolean> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http
          .post<{ success: boolean; data: WordUsageStats }>(
            `${environment.apiUrl}/api/word-usage/track-session`,
            {},
            { headers }
          )
          .pipe(
            map((response) => {
              if (response.success) {
                // Recalculate isOverLimit and usagePercentage to ensure accuracy
                this.updateUsagePercentage(response.data);
                this.usageStatsSubject.next(response.data);
                console.log('📱 Session tracked successfully');
                return true;
              }
              return false;
            }),
            catchError((error) => {
              console.error('❌ Failed to track session:', error);
              return throwError(() => error);
            })
          );
      }),
      catchError((error) => {
        console.error(
          '❌ Failed to get auth headers for session tracking:',
          error
        );
        return throwError(() => error);
      })
    );
  }

  // Update toggle preference in database
  updateTogglePreference(showRemainingView: boolean): Observable<any> {
    console.log('🔄 Updating toggle preference:', showRemainingView);

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${environment.apiUrl}/api/word-usage/toggle-preference`,
          { showRemainingView },
          { headers }
        );
      }),
      tap((response) => {
        console.log('✅ Toggle preference updated successfully:', response);
      }),
      catchError((error) => {
        console.error('❌ Error updating toggle preference:', error);
        return throwError(() => error);
      })
    );
  }

  // Purchase words to add to word limit
  purchaseWords(
    wordsAmount: number,
    packageType: 'one-time' | 'monthly' | 'yearly' = 'one-time',
    packageId?: string,
    price?: number
  ): Observable<any> {
    console.log('💰 Purchasing words:', wordsAmount);
    console.log('💰 Package type:', packageType);
    console.log('💰 Package ID:', packageId);
    console.log('💰 Price:', price);
    console.log('💰 API URL:', `${environment.apiUrl}/api/word-usage/purchase`);

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        console.log('💰 Making purchase request with headers:', headers);
        console.log('💰 Headers map:', headers.keys());
        console.log(
          '💰 Authorization header present:',
          headers.has('Authorization')
        );
        return this.http.post<any>(
          `${environment.apiUrl}/api/word-usage/purchase`,
          {
            words: wordsAmount,
            packageType,
            packageId,
            price,
          },
          { headers }
        );
      }),
      tap((response) => {
        if (response.success) {
          console.log('✅ Words purchased successfully:', response.data);
          // Update local data with new limits
          this.quotaSettingsSubject.next(response.data.settings);
          // Recalculate isOverLimit and usagePercentage to ensure accuracy
          this.updateUsagePercentage(response.data.stats);
          this.usageStatsSubject.next(response.data.stats);
        }
      }),
      catchError((error) => {
        console.error('❌ Error purchasing words:', error);
        return throwError(() => error);
      })
    );
  }

  // Handle authentication errors with toast notification and automatic logout
  private async handleAuthError(error: any): Promise<void> {
    console.error('🔐 WordUsageService: Authentication error:', error);
    console.log(
      '🚨 WordUsageService handleAuthError called - starting automatic logout process'
    );

    // First, immediately start the logout process
    console.log('🚪 WordUsageService: Starting immediate logout...');
    this.authService.logout();
    console.log(
      '✅ WordUsageService: User logged out due to authentication error'
    );

    // Then try to show notification (non-blocking)
    console.log(
      '🍞 WordUsageService: Attempting to show toast notification (non-blocking)...'
    );
    this.showNotificationAsync(
      'Your session has expired. Please log in again.'
    );

    // Also try a simple browser alert as backup
    setTimeout(() => {
      console.log(
        '🔔 WordUsageService: Showing browser alert as backup notification...'
      );
      alert('Your session has expired. Please log in again.');
    }, 500);

    // Force change detection to ensure UI updates
    setTimeout(() => {
      console.log('🔄 WordUsageService: Forcing change detection after logout');
      // Trigger a small change to force Angular change detection
      this.authService.currentUser$.subscribe((user) => {
        console.log('🔍 WordUsageService: Current user after logout:', user);
      });

      // Additional force logout attempt
      console.log('🔄 WordUsageService: Additional logout attempt...');
      this.authService.logout();
    }, 100);
  }

  // Non-blocking notification method
  private async showNotificationAsync(message: string): Promise<void> {
    try {
      console.log('🍞 WordUsageService: Attempting toast notification...');
      await this.toastService.showError(message, 5000);
      console.log('✅ WordUsageService: Toast notification shown successfully');
    } catch (toastError) {
      console.error('❌ WordUsageService: Toast error:', toastError);
      console.log('⚠️ WordUsageService: Using browser alert as fallback...');
      // Use browser alert as fallback
      alert(message);
    }
  }

  // Confirm Stripe payment and update user quotas
  confirmStripePayment(paymentIntentId: string): Observable<any> {
    console.log('💳 Confirming Stripe payment:', paymentIntentId);

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${environment.apiUrl}/api/stripe/confirm-payment`,
          { paymentIntentId },
          { headers }
        );
      }),
      tap((response) => {
        if (response.success) {
          console.log(
            '✅ Stripe payment confirmed successfully:',
            response.data
          );
          // Reload usage stats to reflect the new limits
          this.loadUsageStats();
          this.loadQuotaSettings();
        }
      }),
      catchError((error) => {
        console.error('❌ Error confirming Stripe payment:', error);
        return throwError(() => error);
      })
    );
  }
}
